import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "@/components/Header/Header";
import { ErrorState } from "@/components/ErrorState/ErrorState";
import { Loading } from "@/components/Loading/Loading";
import { SwfPlayer } from "@/features/swf-player/SwfPlayer";
import type { SwfEvent } from "@/features/swf-player/swfTypes";
import { lessonApi } from "@/services/lessonApi";
import { progressApi } from "@/services/progressApi";
import { useAuthStore } from "@/store/authStore";
import { useProgressStore } from "@/store/progressStore";
import { useDebouncedCallback } from "@/hooks/useDebounce";

const PROGRESS_SYNC_INTERVAL_MS = Number(
  import.meta.env.VITE_PROGRESS_SYNC_INTERVAL_MS ?? 5000,
);

export function Player() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const student = useAuthStore((s) => s.session?.student);
  const setProgress = useProgressStore((s) => s.setProgress);
  const setScore = useProgressStore((s) => s.setScore);
  const setCompleted = useProgressStore((s) => s.setCompleted);

  const [swfUrl, setSwfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const startedAt = useRef<number>(Date.now());
  const latestScore = useRef<{ score: number; total: number } | null>(null);

  useEffect(() => {
    if (!lessonId) return;
    startedAt.current = Date.now();
    lessonApi
      .getLessonContent(lessonId)
      .then((content) => setSwfUrl(content.swfUrl))
      .catch(() =>
        setError(
          "This lesson could not be loaded. It may need to be updated for this device.",
        ),
      );
  }, [lessonId]);

  const syncProgress = useDebouncedCallback((progress: number) => {
    if (!lessonId || !student) return;
    progressApi
      .updateProgress({
        lessonId,
        studentId: student.id,
        progress,
        completed: false,
        score: latestScore.current?.score,
        totalQuestions: latestScore.current?.total,
      })
      .catch(() => {
        // Non-fatal: local state already reflects the update; next sync retries.
      });
  }, PROGRESS_SYNC_INTERVAL_MS);

  const handleEvent = useCallback(
    (event: SwfEvent) => {
      if (!lessonId) return;
      switch (event.type) {
        case "PROGRESS":
          setProgress(lessonId, event.value);
          syncProgress(event.value);
          break;
        case "SCORE":
          latestScore.current = {
            score: event.value,
            total: latestScore.current?.total ?? 0,
          };
          setScore(lessonId, event.value);
          break;
        default:
          break;
      }
    },
    [lessonId, setProgress, setScore, syncProgress],
  );

  const handleComplete = useCallback(() => {
    if (!lessonId || !student) return;
    setCompleted(lessonId);
    progressApi
      .completeLesson({
        lessonId,
        studentId: student.id,
        score: latestScore.current?.score,
        totalQuestions: latestScore.current?.total,
        timeSpentSeconds: Math.round((Date.now() - startedAt.current) / 1000),
      })
      .catch(() => {
        // Retry strategy / offline queue would go here in production.
      });
  }, [lessonId, student, setCompleted]);

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden bg-slate-900">
      <Header
        title={lessonId === "fnat" ? "FNAT" : "Lesson"}
        showBack
      />

      <div className="hidden portrait:flex sm:portrait:hidden items-center justify-center bg-amber-400 text-slate-900 text-xs font-bold py-2 px-4 gap-2 shadow-inner z-50">
        <span>🔄</span>
        <span>For the best experience, please rotate your device to landscape mode.</span>
      </div>

      <main className="flex min-h-0 flex-1 flex-col overflow-hidden p-2 sm:p-4">
        {error && (
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden">
            <ErrorState
              message={error}
              onRetry={() => navigate(0)}
            />
          </div>
        )}

        {!error && !swfUrl && (
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden">
            <Loading label="Fetching your lesson..." />
          </div>
        )}

        {!error && swfUrl && (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <SwfPlayer
              swfUrl={swfUrl}
              onComplete={handleComplete}
              onEvent={handleEvent}
              onError={(e) => setError(e.message)}
            />
          </div>
        )}
      </main>
    </div>
  );
}
